import { CircularProgress, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FilterTypes } from "../../enums/components";
import { chartTypesEnums } from "../../enums/dynamicDashboard";
import {
  categoricalColumns,
  columnsAPINamesEnums,
  columnsToExclude,
  columnsUnits,
  defaultColors,
  dropdownFilters,
  FiltersNames,
  getFinancialDashboardColumnDisplayName,
  isRelevantOption,
  numericRangeFilters,
} from "../../enums/Modernization/financialDashboard";
import { deleteKeysFromObjects, distinctFilter } from "../../utils/dataManipulation";
import {
  getChartFiltersCreators,
  getColorEntity,
  getDynamicDahboardDefaultSettings,
  getTabFiltersCreators,
  handleConfigChange,
  settingsOptions,
} from "../../utils/dynamicDashboard";
import { Button } from "../ButtonNew";
import { DynamicDashboardMultiTab } from "../chart/DynamicDashboardMultiTab";
import { CustomModal } from "../CustomModal";
import { DividerHorizontal } from "../Divider";
import { LoadingOrEmptyWrapper } from "../LoadingAndEmptyHandler";

const filtersCategories = [
  {
    categoryName: "Basic Criteria",
    filtersNames: [
      columnsAPINamesEnums.ADAPTATION,
      columnsAPINamesEnums.COMPONENT,
      columnsAPINamesEnums.SUB_COMPONENT,
      columnsAPINamesEnums.TYPE_OF_MESSURE,
    ],
  },
  {
    categoryName: "Energetic effect",
    filtersNames: [
      columnsAPINamesEnums.REDUCTION_ELECTRICITY_END,
      columnsAPINamesEnums.REDUCTION_ELECTRICITY_PRIMARY,
      columnsAPINamesEnums.REDUCTION_ENERGY_DEMAND,
      columnsAPINamesEnums.REDUCTION_ENERGY_END,
      columnsAPINamesEnums.REDUCTION_ENERGY_PRIMARY,
      columnsAPINamesEnums.REDUCTION_HEAT_END,
      columnsAPINamesEnums.REDUCTION_HEAT_PRIMARY,
      columnsAPINamesEnums.ELECTRICITY_REDUCTION,
      columnsAPINamesEnums.HEAT_REDUCTION,
    ],
  },
  {
    categoryName: "Environmental Effect",
    filtersNames: [columnsAPINamesEnums.CARBON_DIOXIDE_SAVINGS, columnsAPINamesEnums.EMBODIED_CARBON],
  },
  {
    categoryName: "Economic KPIs",
    filtersNames: [columnsAPINamesEnums.ROI, columnsAPINamesEnums.INFLATION_RATE],
  },
  {
    categoryName: "Economic benefits",
    filtersNames: [
      columnsAPINamesEnums.ELECTRICITY_COST_SAVINGS,
      columnsAPINamesEnums.HEAT_ENERGY_COST_SAVINGS,
      columnsAPINamesEnums.OTHER_COST_SAVINGS,
      columnsAPINamesEnums.COST_SAVINGS,
    ],
  },
  {
    categoryName: "Operating Costs",
    filtersNames: [columnsAPINamesEnums.MAINTENANCE_COST, columnsAPINamesEnums.SERVICES_COST],
  },
  {
    categoryName: "Investment Costs",
    filtersNames: [
      columnsAPINamesEnums.LOAN_TYPE,
      columnsAPINamesEnums.FOEIGN_CAPITAL,
      columnsAPINamesEnums.OWN_CAPITAL,
      columnsAPINamesEnums.INVESTMENTS,
      columnsAPINamesEnums.INTEREST_RATE,
      columnsAPINamesEnums.REPAYMENT_TIME,
      columnsAPINamesEnums.SUBSIDIES,
    ],
  },
];

const defaultPortfolioConfigsAndColorsConfigs = {
  colors: {
    Columns: {},
    Mixed: {},
    Values: {},
  },
  tabs: [
    {
      filters: {},
      selectedFiltersNames: [],
      settings: [],
      tab: "Overview",
    },
  ],
};

export const FinancialDashbaord = ({
  data,
  isAdding,
  isDeleting,
  isLoading,
  onAddRetrofit,
  onDeleteRetrofit,
  projectId,
  selectedRetrofits,
}) => {
  const { t } = useTranslation();
  const dataColumns = Object.values(columnsAPINamesEnums).filter((col) => !columnsToExclude.includes(col));
  let { generalDashboardData, numericalColumns } = useMemo(() => {
    const allColumns = Object.keys(data?.[0] ?? {});
    const toBeRemovedColumns = allColumns?.filter((col) => !dataColumns.includes(col));

    const shreinkedData = deleteKeysFromObjects(data ?? [], toBeRemovedColumns);
    const generalDashboardData = shreinkedData.map((row) => {
      return {
        ...row,
        [columnsAPINamesEnums.TOTAL_ENERGY_REDUCTION]:
          row[columnsAPINamesEnums.ELECTRICITY_REDUCTIONN] + row[columnsAPINamesEnums.HEAT_REDUCTION],
      };
    });
    const numericalColumns = dataColumns.filter((col) => !categoricalColumns.includes(col));
    return { generalDashboardData, numericalColumns };
  }, [data]);

  const [filteredData, setFilteredData] = useState(generalDashboardData);

  const memoizedSpecificData = useMemo(() => [], []);
  const useGetSpecificData = useCallback((_config) => {
    return { isError: false, isLoading: false, specificData: memoizedSpecificData };
  }, []);

  const getDataTransformatorMemoDependencyArray = useCallback(({ config }) => {
    const { chartType, dataPoint, diagrams, xAxis, yAxis } = config;

    return JSON.stringify([dataPoint, chartType, diagrams, xAxis, yAxis]);
  }, []);

  const dataTransformator = useCallback((data, { chartType, dataPoint, diagrams, xAxis, yAxis }) => {
    let output = [];
    const { partitions, stacks } = diagrams?.[0] ?? {};
    if ([chartTypesEnums.DOUGHNUT, chartTypesEnums.PIE].includes(chartType)) {
      const flattenData = data?.map((row) => {
        const filteredRow = {};
        if (partitions) filteredRow[partitions] = row[partitions];
        if (stacks) filteredRow[stacks] = row[stacks];
        return {
          ...filteredRow,
          dataPoint,
          [dataPoint]: row[dataPoint],
        };
      });
      output = { flattenData: flattenData || [] };
    } else if (
      [chartTypesEnums.AREA, chartTypesEnums.BAR, chartTypesEnums.KPI, chartTypesEnums.LINE].includes(chartType)
    ) {
      const flattenData = data?.map((row) => {
        const filteredRow = {};
        if (partitions) filteredRow[partitions] = row[partitions];
        if (stacks) filteredRow[stacks] = row[stacks];
        if (xAxis) filteredRow[xAxis] = row[xAxis];
        return {
          ...filteredRow,
          dataPoint,
          [dataPoint]: row[dataPoint],
        };
      });
      output = { flattenData: flattenData || [] };
    } else if (chartType === chartTypesEnums.HEATMAP) {
      const flattenData = data?.map((row) => {
        const filteredRow = {};
        if (yAxis) filteredRow[yAxis] = row[yAxis];
        if (xAxis) filteredRow[xAxis] = row[xAxis];
        return {
          ...filteredRow,
          dataPoint,
          [dataPoint]: row[dataPoint],
        };
      });
      const xLabels = data?.map((row) => row[xAxis])?.filter(distinctFilter);
      xLabels?.sort();

      const yLabels = data?.map((row) => row[yAxis])?.filter(distinctFilter);
      yLabels?.sort();

      output = { flattenData: flattenData || [], xLabels, yLabels };
    } else {
      output = { flattenData: data || [] };
    }

    return output;
  }, []);

  const dynamicChartsRowHeight = 50;

  const getUnit = useCallback(({ quantity }) => columnsUnits?.[quantity], []);
  const getColumnDisplayNameCallback = useCallback((props) => getFinancialDashboardColumnDisplayName(props), []);

  const dataColumnsDisplayOptions = dataColumns?.map((colName) => getFinancialDashboardColumnDisplayName({ colName }));

  const allSettings = (updateChart, config) =>
    !config
      ? []
      : [
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.TITLE, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.DESCRIPTION, updateChart }),
          {
            adjustConfig: (config) => config,
            filterLabel: "Metric",
            filterName: FiltersNames.DATAPOINT,
            filterType: FilterTypes.DROPDOWN_SINGLE,
            isInDiagramsPart: false,
            mainDataKey: "dataPoint",
            props: {
              displayOptions: dataColumnsDisplayOptions,
              label: "Metric",
              options: dataColumns,
              width: "75%",
            },
            setConfig: (updateChart) => updateChart,
            visible: () => isRelevantOption(config.chartType, settingsOptions.DATA_POINT),
          },
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.Y_AXIS_NAME, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.SECOND_Y_AXIS_NAME, updateChart }),
          {
            adjustConfig: (config) => config,
            filterLabel: "X Axis",
            filterName: FiltersNames.X_AXIS,
            filterType: FilterTypes.DROPDOWN_SINGLE,
            isInDiagramsPart: false,
            props: {
              className: "w-3/4",
              displayOptions: [...dataColumnsDisplayOptions],
              label: "x-axis",
              options: [...dataColumns],
            },
            setConfig: (updateChart) => updateChart,
            visible: () =>
              [chartTypesEnums.COMPOSED, chartTypesEnums.SCATTER].includes(config.chartType) &&
              isRelevantOption(config.chartType, settingsOptions.X_AXIS),
          },
          {
            adjustConfig: (config) => config,
            filterLabel: "X Axis",
            filterName: FiltersNames.X_AXIS,
            filterType: FilterTypes.DROPDOWN_SINGLE,
            isInDiagramsPart: false,
            props: {
              className: "w-3/4",
              displayOptions: [...dataColumnsDisplayOptions],
              label: "x-axis",
              options: [...dataColumns],
            },
            setConfig: (updateChart) => updateChart,
            visible: () =>
              [chartTypesEnums.AREA, chartTypesEnums.BAR, chartTypesEnums.HEATMAP, chartTypesEnums.LINE].includes(
                config.chartType
              ) && isRelevantOption(config.chartType, settingsOptions.X_AXIS),
          },
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.X_AXIS_NAME, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.INVERT_X_AXIS, updateChart }),
          {
            adjustConfig: (config) => config,
            filterLabel: "Y Axis",
            filterName: FiltersNames.Y_AXIS,
            filterType: FilterTypes.DROPDOWN_SINGLE,
            isInDiagramsPart: false,
            props: {
              className: "w-3/4",
              displayOptions: [...dataColumnsDisplayOptions],
              label: "y-axis",
              options: [...dataColumns],
            },
            setConfig: (updateChart) => updateChart,
            visible: () => isRelevantOption(config.chartType, settingsOptions.Y_AXIS),
          },
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.INVERT_Y_AXIS, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.X_AXIS_RANGE, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.Y_AXIS_RANGE, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.SECOND_Y_AXIS_RANGE, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.LEGENDS_POSITION, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.SHOW_PIE_LABELS, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.SORT_VALUE, updateChart }),
          {
            adjustConfig: (config) => config,
            adjustSetConfigFn: (index) => (filterName, value) =>
              handleConfigChange(index, config.diagrams, filterName, value, updateChart),
            filterLabel: "Y Axis",
            filterName: FiltersNames.DIAGRAMS_DIAGRAM_DATA_KEY,
            filterType: FilterTypes.DROPDOWN_SINGLE,
            isInDiagramsPart: true,
            props: {
              className: "block w-3/4 ",
              displayOptions: [...dataColumnsDisplayOptions],
              label: "Y Axis",
              options: [...dataColumns],
            },
            visible: (diagramIndex) =>
              isRelevantOption(config.chartType, settingsOptions.DIAGRAMS_DIAGRAM_DATA_KEY, diagramIndex),
          },
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.DIAGRAM_TYPE, updateChart }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.DIAGRAMS_STACK_ID, updateChart }),
          getDynamicDahboardDefaultSettings({
            config,
            settingName: settingsOptions.DIAGRAMS_AS_SECOND_Y_AXIS,
            updateChart,
          }),
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.AGGREGATION_TYPE, updateChart }),
          {
            adjustConfig: (config) => config,
            adjustSetConfigFn: (index) => (filterName, value) =>
              handleConfigChange(index, config.diagrams, filterName, value, updateChart),
            filterLabel: "Partitions",
            filterName: FiltersNames.DIAGRAMS_PARTITIONS,
            filterType: FilterTypes.DROPDOWN_SINGLE,
            isInDiagramsPart: true,
            props: {
              className: "block w-3/4 ",
              displayOptions: [...dataColumnsDisplayOptions],
              label: "Partitions",
              options: [...dataColumns],
            },
            visible: (diagramIndex) => isRelevantOption(config.chartType, settingsOptions.PARTITIONS, diagramIndex),
          },
          {
            adjustConfig: (config) => config,
            adjustSetConfigFn: (index) => (filterName, value) =>
              handleConfigChange(index, config.diagrams, filterName, value, updateChart),
            filterLabel: "Stacks",
            filterName: FiltersNames.DIAGRAMS_STACKS,
            filterType: FilterTypes.DROPDOWN_SINGLE,
            isInDiagramsPart: true,
            props: {
              className: "block w-3/4 ",
              displayOptions: [...dataColumnsDisplayOptions],
              label: "Stacks",
              options: [...dataColumns],
            },
            visible: (diagramIndex) => isRelevantOption(config.chartType, settingsOptions.STACKS, diagramIndex),
          },
          getDynamicDahboardDefaultSettings({ config, settingName: settingsOptions.DIAGRAMS_DIRECTION, updateChart }),
        ];

  const nameForLocalStorage = `financialDashboardConfigs-${projectId}`;

  const loadDashboardConfigs = () => {
    let dashboardConfigs = localStorage.getItem(nameForLocalStorage);
    if (dashboardConfigs) {
      dashboardConfigs = JSON.parse(dashboardConfigs);
    } else {
      dashboardConfigs = defaultPortfolioConfigsAndColorsConfigs;
    }
    return dashboardConfigs;
  };

  let savedPortfolioAndColorsConfigsMemoized = useMemo(() => {
    let savedPortfolioAndColorsConfigs = loadDashboardConfigs(projectId);
    return savedPortfolioAndColorsConfigs;
  }, []);

  const onResetDashboardConfigs = () => {
    localStorage.removeItem(nameForLocalStorage);
  };

  const saveDashboardConfigs = (data) => {
    localStorage.setItem(nameForLocalStorage, JSON.stringify(data));
  };

  const chartTypes = useMemo(
    () => [
      chartTypesEnums.AREA,
      chartTypesEnums.BAR,
      chartTypesEnums.COMPOSED,
      chartTypesEnums.SCATTER,
      chartTypesEnums.DOUGHNUT,
      chartTypesEnums.HEATMAP,
      chartTypesEnums.HISTOGRAM,
      chartTypesEnums.KPI,
      chartTypesEnums.LINE,
      chartTypesEnums.PIE,
    ],
    []
  );

  const renderChartsObjectClickModal = ({ clickedObject, relatedObjects, setClickedObject }) => {
    return (
      <CustomModal
        className="relative"
        disableScrollLock
        modalOpen={clickedObject}
        setModalOpen={setClickedObject}
        width={700}
      >
        <h3 className="font-ds-headline-lg ">{t("Modernization.wizard.retrofitMeasures")}</h3>
        {(isAdding || isDeleting) && (
          <Stack className="absolute left-4  top-4 gap-1">
            <CircularProgress className="!h-3 !w-3 " />
            <span className=" font-ds-body-regular-xs">
              {isAdding ? t("general.adding") : t("general.deleting")}...
            </span>
          </Stack>
        )}
        <Stack className="mt-6 !h-70 flex-col gap-2 overflow-auto pr-2">
          {relatedObjects?.map((obj) => {
            const nameCol = columnsAPINamesEnums.NAME;
            const isAlreadySelected = selectedRetrofits?.map((r) => r[nameCol])?.includes(obj[nameCol]);
            return (
              <>
                <Stack className="w-full items-center justify-between">
                  <span className="inline-block w-3/4 truncate text-left font-ds-body-semibold-md">
                    {obj?.[nameCol]}
                  </span>
                  {!isAlreadySelected ? (
                    <Button
                      disabled={isAdding || isDeleting}
                      onClick={() => {
                        onAddRetrofit(obj);
                      }}
                    >
                      + {t("Modernization.wizard.addRetrofit")}
                    </Button>
                  ) : (
                    <Button
                      disabled={isAdding || isDeleting}
                      onClick={() => {
                        onDeleteRetrofit(obj);
                      }}
                      variant="error"
                    >
                      - {t("Modernization.wizard.deleteRetrofit")}
                    </Button>
                  )}
                </Stack>
                <DividerHorizontal />
              </>
            );
          })}
        </Stack>
      </CustomModal>
    );
  };

  const tabFilters = ({ allFiltersConfig, setConfig }) => {
    const { createDropdownFilter, createNumericRangeFilter } = getTabFiltersCreators({
      allFiltersConfig,
      columnsDisplayNameFn: getFinancialDashboardColumnDisplayName,
      generalDashboardData,
      setConfig,
    });

    return [...dropdownFilters.map(createDropdownFilter), ...numericRangeFilters.map(createNumericRangeFilter)];
  };

  const chartFilters = useCallback(
    (updateChart, config) => {
      const { createDropdownFilter, createNumericRangeFilter } = getChartFiltersCreators({
        columnsDisplayNameFn: getFinancialDashboardColumnDisplayName,
        config,
        generalDashboardData,
        updateChart,
      });

      return [...dropdownFilters.map(createDropdownFilter), ...numericRangeFilters.map(createNumericRangeFilter)];
    },
    [generalDashboardData]
  );

  return (
    <LoadingOrEmptyWrapper height="400px" showLoading={isLoading}>
      <DynamicDashboardMultiTab
        allAvailableFilters={tabFilters}
        allFilters={chartFilters}
        allSettings={allSettings}
        categoricalColumns={categoricalColumns}
        categoricalFilter
        chartTypes={chartTypes}
        dataTransformator={dataTransformator}
        defaultColors={defaultColors}
        defaultPortfolioConfigsAndColorsConfigs={defaultPortfolioConfigsAndColorsConfigs}
        dynamicChartsRowHeight={dynamicChartsRowHeight}
        filteredData={filteredData}
        filtersCategories={filtersCategories}
        generalDashboardData={generalDashboardData}
        getColorEntity={getColorEntity}
        getColumnDisplayName={getColumnDisplayNameCallback}
        getDataTransformatorMemoDependencyArray={getDataTransformatorMemoDependencyArray}
        getUnit={getUnit}
        IdColumnName={columnsAPINamesEnums.ID}
        multithread={false}
        numericalColumns={numericalColumns}
        onReset={onResetDashboardConfigs}
        onSave={saveDashboardConfigs}
        renderChartsObjectClickModal={renderChartsObjectClickModal}
        savedPortfolioAndColorsConfigs={savedPortfolioAndColorsConfigsMemoized}
        setFilteredData={setFilteredData}
        setTriggerFlag={() => {}}
        useGetSpecificData={useGetSpecificData}
      />
    </LoadingOrEmptyWrapper>
  );
};
